// ProcessSteps.js
import React from 'react';
import { motion } from 'framer-motion';

const ProcessSteps = () => {
  const steps = [
    {
      number: '01',
      title: 'Free Initial Consultation',
      subtitle: 'Your journey begins with a free consultation',
      points: [
        'Review your academic background and goals',
        'Discuss preferred destinations and programs',
        'Assess eligibility and realistic options',
        'Explain timelines, requirements, and next steps'
      ],
      footer: 'This ensures clarity before any commitment is made.'
    },
    {
      number: '02',
      title: 'Matching and Institutional Offers',
      subtitle: 'Based on your profile, we:',
      points: [
        'Match you with suitable institutions and programs',
        'Engage partner schools where appropriate',
        'Identify available scholarship or tuition discount opportunities'
      ],
      footer: 'All recommendations are made transparently and based on eligibility.'
    },
    {
      number: '03',
      title: 'Application and Documentation Support',
      subtitle: 'Once a suitable pathway is confirmed, our team handles the application process on your behalf, including:',
      points: [
        'Preparing and submitting applications',
        'Drafting and refining your Statement of Purpose',
        'Organising academic and supporting documents',
        'Coordinating submissions with institutions'
      ],
      footer: 'You are kept informed throughout the process.'
    },
    {
      number: '04',
      title: 'Direct Communication with Institutions',
      subtitle: 'Once contact is established:',
      points: [
        'Admission communication is between you and the institution',
        'Admission letters and scholarship offers are issued directly to you',
        'Tuition and related fees are paid directly to the school'
      ],
      footer: 'Notapaths does not collect tuition payments on behalf of institutions. We remain available to guide and support you at every stage.'
    },
    {
      number: '05',
      title: 'Ongoing Guidance and Preparation',
      subtitle: 'After admission, we continue to support you by:',
      points: [
        'Explaining offer terms and timelines',
        'Guiding acceptance and enrolment steps',
        'Supporting preparation for relocation and study readiness'
      ],
      footer: 'Our goal is to ensure you move forward with clarity and confidence.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-[#F5F8FA] text-[#2542D4] text-sm font-light px-6 py-2 rounded-full mb-4">
            How It Works
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Our Study Abroad Pipeline
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            A clear, structured process from consultation to admission and beyond
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div className="grid md:grid-cols-12 gap-6 items-start">
                  {/* Left Side - Number Only */}
                  <div className="md:col-span-2 flex items-center md:items-start">
                    <div className="flex-shrink-0">
                      <div className="text-6xl font-light text-[#202356]/10">
                        {step.number}
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Content */}
                  <div className="md:col-span-10">
                    <div className="bg-[#F5F8FA] p-8 rounded-lg">
                      <h3 className="text-2xl font-semibold text-[#202356] mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 text-sm font-light mb-4">
                        {step.subtitle}
                      </p>
                      
                      {/* Points List */}
                      <ul className="space-y-2">
                        {step.points.map((point, pointIndex) => (
                          <li key={pointIndex} className="flex items-start gap-2">
                            <span className="text-[#202356]">•</span>
                            <span className="text-gray-600 text-sm font-light">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;
